import React from 'react';

/**
 * DndLoader - themed clashing-swords loading animation.
 *
 * Props:
 *  - fullScreen (bool): render as a centered full-viewport overlay (default true)
 *  - label (string): optional text shown beneath the animation
 *  - size ("sm" | "md" | "lg"): scale of the swords (default "md")
 *  - className: extra classes for the wrapper
 */
const SIZE_MAP = {
  sm: { box: 'w-28 h-28', sword: 'w-12', dot: 'w-1.5 h-1.5', gap: 'gap-3' },
  md: { box: 'w-48 h-48', sword: 'w-20', dot: 'w-2 h-2', gap: 'gap-5' },
  lg: { box: 'w-64 h-64', sword: 'w-28', dot: 'w-2.5 h-2.5', gap: 'gap-6' },
};

const SwordSvg = ({ className }) => (
  <svg
    viewBox="0 -60 100 220"
    className={`${className} h-auto drop-shadow-[0_0_12px_rgba(124,58,237,1)]`}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {/* Blade */}
    <path d="M50,-50 L44,40 L46.5,105 L53.5,105 L56,40 Z" strokeWidth="2" fill="#7c3aed" fillOpacity="0.15" />
    <line x1="50" y1="-50" x2="50" y2="105" strokeWidth="2" />
    <line x1="47.5" y1="40" x2="47.5" y2="100" strokeWidth="1" strokeOpacity="0.4" />
    <line x1="52.5" y1="40" x2="52.5" y2="100" strokeWidth="1" strokeOpacity="0.4" />
    {/* Crossguard */}
    <path d="M10,95 Q50,115 90,95 L85,105 Q50,120 15,105 Z" strokeWidth="2" fill="#0E0E10" />
    {/* Handle */}
    <rect x="42" y="112" width="16" height="30" rx="2" strokeWidth="2" fill="#0E0E10" />
    <line x1="42" y1="118" x2="58" y2="115" strokeWidth="1.5" />
    <line x1="42" y1="125" x2="58" y2="122" strokeWidth="1.5" />
    <line x1="42" y1="132" x2="58" y2="129" strokeWidth="1.5" />
    {/* Pommel */}
    <polygon points="50,142 40,152 50,158 60,152" strokeWidth="2" fill="#0E0E10" />
  </svg>
);

export default function DndLoader({ fullScreen = true, label, size = 'md', className = '' }) {
  const s = SIZE_MAP[size] || SIZE_MAP.md;

  const wrapperClasses = fullScreen
    ? `flex flex-col items-center justify-center min-h-screen w-full bg-neutral-950 ${className}`
    : `flex flex-col items-center justify-center ${className}`;

  return (
    <div className={wrapperClasses} data-name="DndLoader" role="status" aria-live="polite" aria-busy="true">
      {/* Scaled down by 50% — pixel-based sword motion scales uniformly */}
      <div className="flex flex-col items-center justify-center scale-50 origin-center -my-4">
        <div className={`relative flex items-center justify-center ${s.box} mb-6`}>
          {/* Left Sword */}
          <div className="absolute animate-sword-left text-[#7c3aed] origin-center z-10">
            <SwordSvg className={s.sword} />
          </div>
          {/* Right Sword */}
          <div className="absolute animate-sword-right text-[#7c3aed] origin-center z-10">
            <SwordSvg className={s.sword} />
          </div>
        </div>

        <div className={`flex items-center justify-center ${s.gap} mt-[2px]`}>
          <div className={`${s.dot} bg-[#7c3aed] rounded-full animate-mystic-pulse`} style={{ animationDelay: '0ms' }}></div>
          <div className={`${s.dot} bg-[#7c3aed] rounded-full animate-mystic-pulse`} style={{ animationDelay: '300ms' }}></div>
          <div className={`${s.dot} bg-[#7c3aed] rounded-full animate-mystic-pulse`} style={{ animationDelay: '600ms' }}></div>
        </div>
      </div>

      {label ? (
        <p className="mt-6 text-neutral-400 font-cinzel tracking-wide text-sm">{label}</p>
      ) : null}
    </div>
  );
}
